import type { Currency, FlowStatus, SubmissionStatus } from "./types";

const amountFormatter = new Intl.NumberFormat("zh-CN", {
  maximumFractionDigits: 8,
  minimumFractionDigits: 2,
});

const timeFormatter = new Intl.DateTimeFormat("zh-CN", {
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  month: "short",
});

export function formatAmountValue(amount: string | null | undefined): string {
  if (!amount) {
    return "--";
  }

  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return amount;
  }

  return amountFormatter.format(numeric);
}

export function formatMoneyLine(amount: string | null | undefined, currency: Currency | null | undefined): string {
  if (!amount || !currency) {
    return "待补充金额与币种";
  }

  return `${formatAmountValue(amount)} ${currency}`;
}

export function formatTimestamp(value: string | undefined): string {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return timeFormatter.format(date);
}

export function sanitizeAmountInput(raw: string): string {
  const next = raw.replace(/[^\d.]/gu, "");
  const firstDot = next.indexOf(".");

  if (firstDot === -1) {
    return next;
  }

  return `${next.slice(0, firstDot + 1)}${next.slice(firstDot + 1).replace(/\./gu, "")}`;
}

export function normalizeAmountInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "";
  }

  const [integerPart, decimalPart = ""] = trimmed.split(".");
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/u, "") || "0";
  const normalizedDecimal = decimalPart.replace(/0+$/u, "");

  return normalizedDecimal ? `${normalizedInteger}.${normalizedDecimal}` : normalizedInteger;
}

export function validateAmount(raw: string): string | null {
  const normalized = normalizeAmountInput(raw);

  if (!normalized) {
    return "请输入金额。";
  }

  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/u.test(normalized)) {
    return "金额必须是正整数或正小数字符串。";
  }

  if (Number(normalized) <= 0) {
    return "金额必须大于 0。";
  }

  return null;
}

export function flowStatusLabel(status: FlowStatus): string {
  switch (status) {
    case "waiting_amount":
      return "等待金额";
    case "waiting_currency":
      return "等待币种";
    case "awaiting_confirmation":
      return "待确认";
    case "submitted":
      return "已提交";
    case "failed":
      return "暂未完成";
    case "cancelled":
      return "已取消";
    default:
      return status;
  }
}

export function submissionStatusLabel(status: SubmissionStatus): string {
  switch (status) {
    case "succeeded":
      return "已提交";
    case "processing":
      return "处理中";
    case "failed":
      return "提交失败";
    default:
      return status;
  }
}

export function statusTone(status: FlowStatus | SubmissionStatus): "success" | "warning" | "danger" | "neutral" {
  if (status === "succeeded" || status === "submitted") {
    return "success";
  }

  if (status === "processing") {
    return "warning";
  }

  if (status === "failed" || status === "cancelled") {
    return "danger";
  }

  return "neutral";
}
